import { entryProblem, normalizeEntry } from '../../../shared/routeAnchor';
import type { MeetingEntry } from '../../../shared/routeAnchor';

/**
 * A Route-search pick as D120 meeting-point input (D120 amendment, CAY-101 option A). Google's
 * address components fill street/unit/city/state/ZIP; the server's Census lookup still derives the
 * coordinates. No Google point, name or clock comes out of here: the label is always empty, so the
 * header shows the street until the rep types a label.
 *
 * Structural input (Google's `AddressComponent` shape) so tests need no `google` fake.
 */

export interface AddressPart {
  longText?: string | null;
  shortText?: string | null;
  types: readonly string[];
}

export type EntryFromPlace =
  | { entry: MeetingEntry }
  /** What could be read, for the typed form to pre-fill; `missing` says which line to finish. */
  | { draft: Partial<MeetingEntry>; missing: 'street' | 'city_zip' };

/** `entryOf`'s limits (shared/routeAnchor.ts): an entry over them is dropped by `sanitizeRoute`. */
export const ENTRY_LIMITS = { street: 200, unit: 40, city: 100 } as const;

const tidy = (s: string | null | undefined) => (s ?? '').trim().replace(/\s+/g, ' ');

export const entryFromComponents = (parts: readonly AddressPart[] | null | undefined): EntryFromPlace => {
  // undefined: not fetched; []: the place has no address. Neither gives a building to look up.
  if (!parts?.length) return { draft: {}, missing: 'street' };
  const part = (type: string) => parts.find((p) => p.types.includes(type));
  const long = (type: string) => tidy(part(type)?.longText ?? part(type)?.shortText);
  const short = (type: string) => tidy(part(type)?.shortText ?? part(type)?.longText);

  const country = short('country');
  if (country && country.toUpperCase() !== 'US') return { draft: {}, missing: 'street' };

  const number = long('street_number');
  // Census answers in USPS abbreviations; the server's normalizer only maps some long forms, so
  // the short form ("Cir", "Pl", "Ter", "Way") is the one that matches.
  const route = short('route');
  const street = number && route ? `${number} ${route}` : '';
  const unitText = long('subpremise');
  const unit = unitText.length <= ENTRY_LIMITS.unit ? unitText : ''; // display-only (D120): drop, never cut
  const city = long('locality') || long('postal_town') || long('sublocality_level_1');
  const state = short('administrative_area_level_1').toUpperCase();
  const zipText = long('postal_code');
  const zip = /^\d{5}$/.test(zipText) ? zipText : '';

  const candidate = normalizeEntry({ street, unit, city, state, zip, label: '' });
  const draft: Partial<MeetingEntry> = {
    ...(candidate.street ? { street: candidate.street.slice(0, ENTRY_LIMITS.street) } : {}),
    ...(candidate.unit ? { unit: candidate.unit } : {}),
    ...(candidate.city ? { city: candidate.city.slice(0, ENTRY_LIMITS.city) } : {}),
    ...(/^[A-Z]{2}$/.test(candidate.state) ? { state: candidate.state } : {}),
    ...(candidate.zip ? { zip: candidate.zip } : {}),
  };
  if (!candidate.street || candidate.street.length > ENTRY_LIMITS.street || entryProblem(candidate))
    return { draft, missing: 'street' };
  if (!candidate.city || candidate.city.length > ENTRY_LIMITS.city || !draft.state || !candidate.zip)
    return { draft, missing: 'city_zip' };
  return { entry: candidate };
};
